import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import MemoryLanePromo from "@/components/MemoryLanePromo";
import { useAuth } from "@/context/AuthContext";
import ShareButton from "@/components/ShareButton";
import InviteDialog from "@/components/InviteDialog";
import StoryBoard from "@/components/StoryBoard";
import GroupMemorials from "@/components/GroupMemorials";
import WhatsAppGroupBox from "@/components/WhatsAppGroupBox";
import BulkInviteDialog from "@/components/BulkInviteDialog";
import AlmaMaterPromo from "@/components/AlmaMaterPromo";
import TalkVibes from "@/components/TalkVibes";
import GroupInstitutionNeeds from "@/components/GroupInstitutionNeeds";
import {
  ArrowLeft, PaperPlaneRight, UsersThree, Lock, Globe, ChatCircleDots, PushPin, Flower,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import SocialPulseBadge from "@/components/SocialPulseBadge";

export default function GroupDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [tab, setTab] = useState("chat");
  const scrollRef = useRef();
  const lastRef = useRef("");

  const loadGroup = () => api.get(`/groups/${id}`).then((r) => setGroup(r.data)).catch(() => setGroup(false));

  useEffect(() => { loadGroup(); }, [id]);

  useEffect(() => {
    if (!group || !group.is_member) return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/groups/${id}/messages`, { params: { after: lastRef.current } });
        if (active && data.length) {
          setMessages((m) => [...m, ...data]);
          lastRef.current = data[data.length - 1].created_at;
        }
      } catch {}
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => { active = false; clearInterval(t); };
  }, [group?.is_member, id]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const join = async () => { await api.post(`/groups/${id}/join`); loadGroup(); };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    const { data } = await api.post(`/groups/${id}/messages`, { text: t });
    setMessages((m) => [...m, data]);
    lastRef.current = data.created_at;
  };

  if (group === null) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (group === false) return <div className="p-12 text-center text-muted-foreground">Community not found.</div>;

  return (
    <main className="mx-auto max-w-4xl px-5 py-6">
      <button onClick={() => nav("/groups")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Communities</button>

      <div className="rounded-t-2xl border border-border/60 bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground"><UsersThree size={26} weight="duotone" /></span>
            <div>
              <h1 className="font-heading text-2xl font-medium">{group.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="secondary" className="rounded-full font-normal capitalize">{group.type}</Badge>
                <span className="flex items-center gap-1">{group.privacy === "private" ? <Lock size={13} /> : <Globe size={13} />} {group.member_count} members</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <ShareButton targetPath={`/groups/${group.id}`} entityType="group" entityId={group.id} title={group.name} label="Invite" />
            {group.is_member && <BulkInviteDialog groupId={group.id} groupName={group.name} />}
            <InviteDialog type="group" id={group.id} label="Invite card" variant="secondary" size="sm" />
            {group.is_member && <Button variant="outline" size="sm" className="rounded-full" onClick={async () => { await api.post(`/groups/${id}/leave`); loadGroup(); }}>Leave</Button>}
          </div>
        </div>
        {group.description && <p className="mt-4 text-sm text-muted-foreground">{group.description}</p>}
        <WhatsAppGroupBox groupId={group.id} groupName={group.name} />
      </div>

      <GroupInstitutionNeeds institutionId={group.institution_id} institutionName={group.institution_name} />

      <AlmaMaterPromo className="mt-4" />

      <div className="mt-6">
        <TalkVibes entityType="group" entityId={id} title={group.name.replace(/ Alumni Association$/, "")}
          subtitle="Alumni talk — reconnect, share memories, plan reunions & find classmates." accent="alumni"
          memberHint={`${group.member_count || 0} members`} />
      </div>

      {/* Tab switch */}
      <div className="flex gap-1 border-x border-border/60 bg-card px-4 pt-2">
        {[["chat", "Chat", ChatCircleDots], ["board", "Storyboard", PushPin], ["memorial", "In Memoriam", Flower]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`group-tab-${key}`}
            className={`flex items-center gap-1.5 rounded-t-lg px-4 py-2.5 text-sm transition-colors ${tab === key ? "bg-muted/20 font-medium text-foreground border-b-2 border-secondary" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon size={16} weight={tab === key ? "fill" : "regular"} /> {label}
          </button>
        ))}
      </div>

      {tab === "board" && (
        <div className="rounded-b-2xl border border-t-0 border-border/60 bg-muted/20 p-6">
          {group.is_member ? <StoryBoard boardType="group" boardId={group.id} title="Community storyboard" />
            : <p className="py-10 text-center text-sm text-muted-foreground">Join this community to see and post on its storyboard.</p>}
        </div>
      )}

      {tab === "memorial" && (
        <div className="rounded-b-2xl border border-t-0 border-border/60 bg-muted/20 p-6">
          <GroupMemorials groupId={group.id} isMember={group.is_member} />
        </div>
      )}

      {/* Chat */}
      {tab === "chat" && (
      <div className="flex h-[60vh] flex-col rounded-b-2xl border border-t-0 border-border/60 bg-muted/20">
        {!group.is_member ? (
          <div className="grid flex-1 place-items-center p-8 text-center">
            <div>
              <ChatCircleDots size={40} className="mx-auto text-muted-foreground" weight="duotone" />
              <p className="mt-3 text-muted-foreground">Join this community to see and join the conversation.</p>
              <Button className="mt-4 rounded-full" onClick={join} data-testid="join-to-chat">Join community</Button>
            </div>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="chat-messages">
              {messages.length === 0 && <p className="pt-8 text-center text-sm text-muted-foreground">No messages yet. Say hello 👋</p>}
              {messages.map((m) => {
                const mine = m.user_id === user.id;
                const initials = (m.user_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
                return (
                  <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
                    <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={fileUrl(m.avatar_file_id)} /><AvatarFallback className="bg-accent text-[11px]">{initials}</AvatarFallback></Avatar>
                    <div className={`max-w-[72%] ${mine ? "items-end text-right" : ""}`}>
                      <div className={`rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border/60"}`}>
                        {!mine && <div className="mb-0.5 flex items-center gap-1 text-xs font-semibold text-secondary">{m.user_name}<SocialPulseBadge verified={m.socialpulse_verified} showLabel={false} /></div>}
                        {m.text}
                      </div>
                      <div className="mt-1 text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="flex gap-2 border-t border-border/60 bg-card p-3">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="rounded-full" data-testid="chat-input" />
              <Button type="submit" size="icon" className="h-10 w-10 shrink-0 rounded-full" data-testid="chat-send"><PaperPlaneRight size={18} weight="fill" /></Button>
            </form>
          </>
        )}
      </div>
      )}
      <MemoryLanePromo label={group?.name} className="mt-10" />
    </main>
  );
}
